// @ts-check

import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from "react";
// @ts-ignore yeah still it's there
import { optionsList } from "react-jsonschema-form/lib/utils";
import fetch from "mibaby-common/util/fetch";
import { Poll } from "./poll.component";
import { getNormalizer } from "./poll.util";

/**
 * @typedef {{
 *  numVotes: number,
 *  [key: string]: number
 * }} PollStat
 */

/**
 * @typedef {{
 *     load: (formEntryName: string) => void,
 *     statsByName: {
 *         [name: string]: PollStat
 *     }
 * }} FormPollContextValue
 */

// the context is used, so that the poll component
// can understand how the data is aggregated
// define a provider with the modeltype and model id
// and wrap your form with it, if you plan to use the poll
/**
 * @type {React.Context<FormPollContextValue>}
 */
const FormPollContext = createContext({
    load: formEntryName => {},
    statsByName: {},
});

const normalize = getNormalizer({
    // in the form world, we use enumOptions as poll answers
    // and the id is the content itself
    // which we will find under `value`
    getId: item => item.value,
});

const createStatsFetcher = pollStatsUrl => async formEntryName => {
    const response = await fetch(pollStatsUrl, {
        method: "POST",
        body: JSON.stringify({ formEntryName }),
    });
    return response.json();
};

/**
 * hook that is usable when attaching a Poll into a Form.
 * it understands the json schema for strings with enumOptions
 * it tries to connect to a predefined provider, which you can
 * define at a top level of your app. the provider
 * expects a url where it can get stats from.
 *
 * @param {{
 *  enumOptions: {value: any}[],
 *  formEntryName: string,
 *  autoLoad?: boolean,
 *  handleVote?: (id: any) => void
 * }} param0
 * @return {[import("./poll.util").NormalizedAnswer[], any, (id: any) => void]}
 */
export const useFormPoll = ({
    enumOptions,
    formEntryName,
    autoLoad = true,
    handleVote: initialHandleVote,
}) => {
    // state for locally updating the stats
    const [localUserVote, setVote] = useState();
    // access to context to retrieve dispatcher and statistics
    const { load, statsByName } = useContext(FormPollContext);
    // pick the statistics for this form field
    const stats = statsByName[formEntryName] || { numVotes: 0 };
    // normalize
    const answers = useMemo(
        () => normalize(enumOptions, stats, stats.numVotes, localUserVote),
        [localUserVote, statsByName[formEntryName]]
    );
    // vote handler
    const handleVote = useCallback(id => {
        setVote(id);
        if (initialHandleVote) {
            initialHandleVote(id);
        }
    }, []);
    // load if autoload is true
    useEffect(() => {
        autoLoad && load(formEntryName);
    }, [autoLoad]);

    return [answers, localUserVote, handleVote];
};

export const FormPollProvider = ({ pollStatsUrl, ...props }) => {
    const [statsByName, setStats] = useState({});
    const fetchStats = useCallback(createStatsFetcher(pollStatsUrl), [pollStatsUrl]);
    const load = useCallback(
        async formEntryName => {
            const data = await fetchStats(formEntryName);
            setStats(stats => ({
                ...stats,
                [formEntryName]: data,
            }));
        },
        [fetchStats]
    );

    // @ts-ignore we do not know why ts triggers error here
    return <FormPollContext.Provider value={{ load, statsByName }} {...props} />;
};

/**
 * Component compatible with react-json-schema-form
 * @param {{
 *  name: string,
 *  schema: any,
 *  onChange: (id:any) => void
 * }} props
 */
export const FormPollField = props => {
    const { schema, name, onChange } = props;

    /** @type {{value:any}[]} */
    const enumOptions = optionsList(schema);

    const [answers, localUserVote, handleVote] = useFormPoll({
        enumOptions,
        formEntryName: name,
        handleVote: onChange,
    });

    return <Poll answers={answers} selected={localUserVote} onVote={handleVote} />;
};
